import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Switch, Route, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FormElems, Modal, Table } from '../../components';
import closeImgIcon from 'static/images/close.svg';
// Actions
import { fetchRecentBulkPaymentUploadRequest, fetchTerminalIdBulkFilesDownloadRequest, generateAndDownloadSampleFileRequest, terminalIdBulkFileUploadRequest } from './redux/TIDOnBoard.actions';
// Styles
import './TerminalOnboard.scss';
//Icons
import uploadIcon from '../../static/images/uploadIcon.svg';
import TID_Sample from './models/BulkUpload/Terminal_ID_Update_Sample_Sheet.xlsx';
import GenerateDownloadSampleFile from './Generate&DownloadSampleFile';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from 'utils/constants/toastMessages';
import { Button } from 'components/form-elements';

const BulkTerminalIdUpload = props => {
    const {
        auth,
        history,
        actions,
        loader: { showLoader },
        terminalsData,
    } = props;

    const { error, bulkTerminalUploadTableData, isLoading } = terminalsData;
    const { pagination } = bulkTerminalUploadTableData || {};

    const pageSizeOptions = [10, 20, 40, 60];
    const [pageCount, setPageCount] = useState(0);
    const [roleStatus, setRoleStatus] = useState('');
    const [filterData, setFilterData] = useState({});
    const [isResetPage, setIsResetPage] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const columns = useMemo(
        () => [
            { header: 'Request ID', accessor: 'requestId' },
            { header: 'Request Purpose', accessor: 'requestPurpose' },
            { header: 'Merchant ID', accessor: 'merchantId' },
            { header: 'Status', accessor: 'status' },
            { header: 'Created Date', accessor: 'createdDate' },
            { header: 'Valid Count', accessor: 'validCount' },
            { header: 'Invalid Count', accessor: 'invalidCount' },
            { header: 'Total Count', accessor: 'totalCount' },
            { header: 'Action', accessor: 'action' },
        ],
        []
    );

    const fetchData = useCallback(
        ({ pageSize, pageIndex }) => {
            // This will get called when the table needs new data
            // You could fetch your data from literally anywhere,
            const data = {
                pageSize,
                pageNo: pageIndex,
            };
            actions.fetchRecentBulkPaymentUploadRequest({
                ...data,
            });
            setPageSize(pageSize);
        },
        [actions]
    );

    const ManageBulkTerminalActions = ({ data, downloadRequest }) => {
        const handleDownload = () => {
            const payload = {};
            payload.requestId = data.original.requestId;
            downloadRequest(payload);
        };

        const isDisabled = data.original.status === 'INITIATED';

        return (
            <div className={`userAction  multiItem ${isDisabled ? 'button-disabled' : ''}`}>
                <button
                    className={`download ${isDisabled ? 'button-disabled' : ''}`}
                    disabled={isDisabled}
                    title={isDisabled ? 'No Download Available for this file' : 'Download file'}
                    onClick={handleDownload}
                />
            </div>
        );
    };

    const onSuccessModal = () => {
        const formData = new FormData();
        formData.append('inputFile', selectedFile);
        // console.log('file in formdata:', formData.get('inputFile')); 

        actions.terminalIdBulkFileUploadRequest(formData);
        setIsUploadOpen(false);

        // Clear the file input after successful upload
        const fileInput = document.getElementById('UploadBinFile');
        if (fileInput) {
            fileInput.value = '';
        }
        setSelectedFile(null);
    };

    const handleFileChange = (value, event) => {
        event.preventDefault();
        
        // Ensure only one file is selected
        const fileInput = event.target;
        const files = fileInput.files;
        
        if (!files || files.length === 0) {
            toast.info('Please select a file');
            return;
        }
        
        if (files.length > 1) {
            toast.info('Please select only one file at a time');
            // Clear the input to prevent multiple file selection
            fileInput.value = '';
            return;
        }
        
        const selectedFile = files[0];
        
        // Validate file type
        if (!/\.(csv|xlsx)$/i.test(selectedFile.name)) {
            toast.info(ERROR_MESSAGES.ONLY_CSV_OR_XLSX_FILE);
            // Clear the input for invalid file type
            fileInput.value = '';
            return;
        }
        
        // Validate file size (optional - adjust size limit as needed)
        const maxSizeInMB = 10; // 10MB limit
        const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
        
        if (selectedFile.size > maxSizeInBytes) {
            toast.info(`File size should be less than ${maxSizeInMB}MB`);
            fileInput.value = '';
            return;
        }
        
        setSelectedFile(selectedFile);
        setIsUploadOpen(true);
    };

    useEffect(() => {
        if (pagination?.totalNumberOfRecords) {
            setPageCount(Math.ceil(pagination.totalNumberOfRecords / pageSize));
        }
    }, [pageSize, pagination.totalNumberOfRecords]);

    return (
        <div className="bulk-terminal-id-upload">
            <Switch>
                <Route exact path="/tid-onboarding/bulk-terminal-id-upload">
                    <Table
                        id="bulk-terminal-id-upload"
                        tableTitle="Bulk Terminal ID Upload"
                        columns={columns}
                        wrapperClass="bulk-terminal-id-upload"
                        isFiltersEnable
                        isPaginationEnable={true}
                        data={bulkTerminalUploadTableData?.tableData || []}
                        filterData={filterData}
                        error={error && error.errorMessage}
                        loading={showLoader || isLoading}
                        roleStatus={roleStatus}
                        pageCount={pageCount}
                        fetchData={fetchData}
                        numberOfRecord={pagination && pagination.totalNumberOfRecords}
                        pageSizeOptions={pageSizeOptions}
                        paginationLabel="Rows per page"
                        onClickTableAction={() => {}}
                        additionalActions={() => (
                            <div className="button-container" style={{ display: 'flex', gap: '10px' }}>
                                {
                                    <GenerateDownloadSampleFile
                                        actions={actions}
                                        filterData={filterData}
                                        tableData={bulkTerminalUploadTableData?.tableData}
                                        isResetPage={isResetPage}
                                        setIsResetPage={setIsResetPage}
                                        showDownloadPopup={showDownloadPopup}
                                        setShowDownloadPopup={setShowDownloadPopup}
                                    />
                                }

                                {
                                    <>
                                        <label className="custom-btn-2" id="bulk-upload-btn" htmlFor="UploadBinFile">
                                            <img src={uploadIcon} alt="Upload" width={18} height={14} />
                                            Upload File
                                        </label>

                                        <form encType="multipart/form-data" action="">
                                            <FormElems.Input
                                                name="uploadFile"
                                                type="file"
                                                id="UploadBinFile"
                                                wrapperClass="no-display"
                                                accept=".csv, .xlsx"
                                                handleChange={handleFileChange}
                                            />
                                        </form>
                                    </>
                                }
                            </div>
                        )}
                        setRoleStatus={setRoleStatus}
                        tableHead="Recent Uploaded"
                        isResetPage={isResetPage}
                        afterResetPage={() => setIsResetPage(false)}
                        userActions={data => (
                            <ManageBulkTerminalActions
                                data={data}
                                downloadRequest={payload => {
                                    actions.fetchTerminalIdBulkFilesDownloadRequest(payload);
                                }}
                            />
                        )}
                        pagePerView={3}
                        tableActionPopup={null}
                    />
                </Route>
            </Switch>

            <Modal modalIsOpen={isUploadOpen} closeModal={() => {
                setIsUploadOpen(false);
                // Clear the file input when modal is cancelled
                const fileInput = document.getElementById('UploadBinFile');
                if (fileInput) {
                    fileInput.value = '';
                }
                setSelectedFile(null);
            }}>
                <main className="upload-file-modal">
                    <div className="modal-header">
                        <p>Confirm File Upload</p>
                        <img src={closeImgIcon} alt="Close" onClick={() => setIsUploadOpen(false)} />
                    </div>

                    <div className="modal-body">
                        <p>
                            You're uploading: <span>{selectedFile?.name} ({selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(2) : ''}) MB</span>
                        </p>
                        <p>We’ll check for formatting and data accuracy.</p>
                    </div>

                    <div className="modal-footer">
                        <Button
                            buttonType="secondary"
                            customClass="rounded-button"
                            text="Cancel"
                            handleClick={() => {
                                setIsUploadOpen(false);
                                // Clear the file input when cancelled
                                const fileInput = document.getElementById('UploadBinFile');
                                if (fileInput) {
                                    fileInput.value = '';
                                }
                                setSelectedFile(null);
                            }}
                        />
                        <Button
                            buttonType="primary"
                            customClass="rounded-button"
                            text="Upload"
                            handleClick={() => onSuccessModal()}
                        />
                    </div>
                </main>
            </Modal>
        </div>
    );
};

const mapStateToProps = ({ form, loader, editData, terminalsData, auth }) => ({
    form,
    loader,
    editData,
    terminalsData,
    auth,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            fetchRecentBulkPaymentUploadRequest,
            terminalIdBulkFileUploadRequest,
            fetchTerminalIdBulkFilesDownloadRequest,
            generateAndDownloadSampleFileRequest
        },
        dispatch
    ),
});

BulkTerminalIdUpload.propTypes = {
    actions: PropTypes.object,
    history: PropTypes.object,
    tidData: PropTypes.object,
    loader: PropTypes.object,
    terminalsData: PropTypes.object,
    auth: PropTypes.object,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(BulkTerminalIdUpload));